import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Ticket, TicketBoardState, TicketProject, TicketSprint, SprintState, TicketStatus, TicketAttachment, TicketLink, LinkRelation, CustomStatus, StatusCategory, DEFAULT_STATUSES, STATUS_COLOR_PALETTE, inverseRelation } from '../types';
import { uid, now } from './utils';
import { getCurrentUserName } from '../components/chips';

// v4: demo/mock seed removed — board starts empty (drops older seeded saves)
const STORAGE_KEY = 'lexauto.tickets.v4';

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Strict Jira hierarchy ───────────────────────────────────────────────────
// Epic (no parent) → Story/Task/Bug (parent must be an Epic)
// Story/Task/Bug → Subtask (parent must be a standard issue, never an Epic)
function parentAllowed(childType: Ticket['type'], parentType: Ticket['type'] | null): boolean {
  if (!parentType) return true;
  if (childType === 'Epic') return false;
  if (childType === 'Subtask') return parentType === 'Story' || parentType === 'Task' || parentType === 'Bug';
  return parentType === 'Epic';
}

function sanitizeParent(ticket: Ticket, byId: Record<string, Ticket>): string | null {
  if (!ticket.parentId) return null;
  const p = byId[ticket.parentId];
  if (!p || p.id === ticket.id || p.projectId !== ticket.projectId) return null;
  if (!parentAllowed(ticket.type, p.type)) return null;
  return ticket.parentId;
}

export type SprintDestination =
  | { kind: 'backlog' }
  | { kind: 'sprint'; sprintId: string }
  | { kind: 'new'; name: string; goal?: string; id?: string };

interface TicketStoreValue extends TicketBoardState {
  // projects
  createProject: (input: { key: string; name: string; description: string; color: string; linkedProjectUid?: string }) => TicketProject;
  // find the ticket workspace linked to a real Lexauto project, creating it on first use
  ensureLinkedProject: (realUid: string, info: { key: string; name: string; description: string; color: string }) => TicketProject;
  updateProject: (id: string, patch: Partial<TicketProject>) => void;
  deleteProject: (id: string) => void;
  clearStore: () => void;
  pruneDeletedProjects: (validLinkedUids: string[]) => void;
  // workflow columns (per project)
  addStatus: (projectId: string, input: { name: string; color?: string; category?: StatusCategory }) => CustomStatus | null;
  loadStatuses: (projectId: string, statuses: CustomStatus[]) => void;
  updateStatus: (projectId: string, statusId: string, patch: Partial<Pick<CustomStatus, 'name' | 'color' | 'category'>>) => void;
  deleteStatus: (projectId: string, statusId: string, moveTo: string) => void;
  moveStatus: (projectId: string, statusId: string, dir: -1 | 1) => void;
  reorderStatuses: (projectId: string, orderedIds: string[]) => void;
  // move tickets between status names without touching columns (rename/heal);
  // rewrites history entries. Returns the number of tickets moved.
  renameTicketStatus: (projectId: string, from: string, to: string) => number;
  isDoneStatus: (projectId: string, status: string) => boolean;
  // sprints
  createSprint: (projectId: string, input: { name: string; goal: string; startDate?: string; endDate?: string; id?: string }) => TicketSprint;
  updateSprint: (id: string, patch: Partial<TicketSprint>) => void;
  deleteSprint: (id: string) => void;
  startSprint: (id: string, dates?: { startDate: string; endDate: string }) => void;
  completeSprint: (id: string, dest?: SprintDestination) => void;
  loadSprints: (projectId: string, sprints: TicketSprint[]) => void;
  // team roster
  addMember: (projectId: string, name: string) => void;
  removeMember: (projectId: string, name: string) => void;
  autoAssign: (projectId: string) => number;
  setAssigneeRequired: (projectId: string, required: boolean) => void;
  // tickets
  createTicket: (projectId: string, input: Partial<Ticket> & { title: string }) => Ticket;
  loadTickets: (projectId: string, tickets: Ticket[], scopeFilter?: string, append?: boolean) => void;
  updateTicket: (id: string, patch: Partial<Ticket>) => void;
  deleteTicket: (id: string) => void;
  moveTicket: (id: string, status: TicketStatus, resolution?: string) => void;
  assignSprint: (ticketId: string, sprintId: string | null) => void;
  moveToBacklog: (ticketId: string) => void;
  addComment: (ticketId: string, author: string, text: string, id?: string) => void;
  updateComment: (ticketId: string, commentId: string, text: string) => void;
  deleteComment: (ticketId: string, commentId: string) => void;
  // Jira-style issue links (mirrored on both tickets). Return updated arrays for backend sync.
  addLink: (sourceId: string, targetId: string, relation: LinkRelation) => { sourceLinks: TicketLink[]; targetLinks: TicketLink[] } | null;
  removeLink: (sourceId: string, linkId: string) => { sourceLinks: TicketLink[]; targetLinks: TicketLink[] } | null;
  addAttachment: (ticketId: string, file: File, author: string) => Promise<void>;
  deleteAttachment: (ticketId: string, attachmentId: string) => void;
}

const TicketStoreCtx = createContext<TicketStoreValue | null>(null);

function loadInitial(): TicketBoardState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as TicketBoardState;
      if (Array.isArray(parsed.projects) && Array.isArray(parsed.tickets)) {
        // migrate older saves: fill new fields + drop hierarchy links that break strict Jira rules
        const byId: Record<string, any> = {};
        parsed.tickets.forEach((t: any) => { byId[t.id] = t; });
        const membersFromTickets = (pid: string) => {
          const set = new Map<string, string>();
          parsed.tickets.forEach((t: any) => {
            const n = (t.assignee || '').trim();
            if (t.projectId === pid && n && ![...set.values()].some((v) => v.toLowerCase() === n.toLowerCase())) set.set(n, n);
          });
          return [...set.values()];
        };
        const projects = (parsed.projects as any[]).map((p) => ({
          ...p,
          statuses: Array.isArray(p.statuses) && p.statuses.length ? p.statuses : DEFAULT_STATUSES.map((s) => ({ ...s })),
          members: Array.isArray(p.members) ? p.members : membersFromTickets(p.id)
        }));
        const projById: Record<string, any> = {};
        projects.forEach((p) => { projById[p.id] = p; });
        return {
          projects,
          sprints: parsed.sprints || [],
          tickets: parsed.tickets.map((t: any) => {
            const history = Array.isArray(t.statusHistory) && t.statusHistory.length
              ? t.statusHistory
              : [{ status: 'Todo', at: t.createdAt || now() }, ...(t.status && t.status !== 'Todo' ? [{ status: t.status, at: t.updatedAt || t.createdAt || now() }] : [])];
            const names = new Set((projById[t.projectId]?.statuses || []).map((s: any) => s.name));
            const next = {
              ...t,
              comments: t.comments || [],
              checklist: t.checklist || [],
              attachments: t.attachments || [],
              labels: t.labels || [],
              links: Array.isArray(t.links) ? t.links : [],
              components: Array.isArray(t.components) ? t.components : [],
              parentId: t.parentId || null,
              // never leave a ticket on a deleted/unknown column
              status: names.size === 0 || names.has(t.status) ? t.status : [...names][0],
              statusHistory: history
            };
            next.parentId = sanitizeParent(next, byId);
            return next;
          })
        };
      }
    }
  } catch { /* ignore */ }
  return { projects: [], sprints: [], tickets: [] };
}

export function TicketStoreProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TicketBoardState>(loadInitial);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch { /* ignore */ }
  }, [state]);

  const value = useMemo<TicketStoreValue>(() => {
    const buildWorkspace = (input: { key: string; name: string; description: string; color: string; linkedProjectUid?: string }, takenKeys: Set<string>): TicketProject => {
      let key = (input.key.trim().toUpperCase().replace(/[^A-Z]/g, '').slice(0, 6) || 'PRJ');
      if (takenKeys.has(key)) {
        let i = 2;
        while (takenKeys.has(`${key}${i}`.slice(0, 6))) i += 1;
        key = `${key}${i}`.slice(0, 6);
      }
      return {
        id: uid(),
        key,
        name: input.name.trim() || 'Untitled project',
        description: input.description || '',
        color: input.color || '#6366f1',
        counter: 0,
        statuses: DEFAULT_STATUSES.map((s) => ({ ...s, id: uid() })),
        members: [],
        linkedProjectUid: input.linkedProjectUid,
        createdAt: now()
      };
    };
    const createProject: TicketStoreValue['createProject'] = (input) => {
      const taken = new Set(state.projects.map((p) => p.key));
      const p = buildWorkspace(input, taken);
      setState((s) => ({ ...s, projects: [...s.projects, p] }));
      return p;
    };
    const ensureLinkedProject: TicketStoreValue['ensureLinkedProject'] = (realUid, info) => {
      const existing = state.projects.find((p) => p.linkedProjectUid === realUid);
      if (existing) return existing;
      const taken = new Set(state.projects.map((p) => p.key));
      const p = buildWorkspace({ ...info, linkedProjectUid: realUid }, taken);
      setState((s) => (s.projects.some((x) => x.linkedProjectUid === realUid) ? s : { ...s, projects: [...s.projects, p] }));
      return p;
    };
    const updateProject: TicketStoreValue['updateProject'] = (id, patch) =>
      setState((s) => ({ ...s, projects: s.projects.map((p) => (p.id === id ? { ...p, ...patch } : p)) }));
    const deleteProject: TicketStoreValue['deleteProject'] = (id) =>
      setState((s) => ({
        projects: s.projects.filter((p) => p.id !== id),
        sprints: s.sprints.filter((sp) => sp.projectId !== id),
        tickets: s.tickets.filter((t) => t.projectId !== id)
      }));
    const clearStore: TicketStoreValue['clearStore'] = () => {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
      setState({ projects: [], sprints: [], tickets: [] });
    };
    const pruneDeletedProjects: TicketStoreValue['pruneDeletedProjects'] = (validLinkedUids) => {
      const validSet = new Set(validLinkedUids);
      setState((s) => {
        const remainingProjects = s.projects.filter((p) => p.linkedProjectUid && validSet.has(p.linkedProjectUid));
        const remainingProjectIds = new Set(remainingProjects.map((p) => p.id));
        if (remainingProjects.length === s.projects.length) return s;
        return {
          projects: remainingProjects,
          sprints: s.sprints.filter((sp) => remainingProjectIds.has(sp.projectId)),
          tickets: s.tickets.filter((t) => remainingProjectIds.has(t.projectId))
        };
      });
    };

    // ── Team roster ──────────────────────────────────────────────────────────
    const addMember: TicketStoreValue['addMember'] = (projectId, name) => {
      const n = name.trim();
      if (!n) return;
      setState((s) => ({
        ...s,
        projects: s.projects.map((p) =>
          p.id === projectId && !(p.members || []).some((m) => m.toLowerCase() === n.toLowerCase())
            ? { ...p, members: [...(p.members || []), n] }
            : p
        )
      }));
    };
    const removeMember: TicketStoreValue['removeMember'] = (projectId, name) =>
      setState((s) => ({
        ...s,
        projects: s.projects.map((p) =>
          p.id === projectId ? { ...p, members: (p.members || []).filter((m) => m !== name) } : p
        )
      }));
    // give every unassigned ticket to the member with the lightest open workload
    const autoAssign: TicketStoreValue['autoAssign'] = (projectId) => {
      const p = state.projects.find((x) => x.id === projectId);
      const roster = p?.members || [];
      if (!p || roster.length === 0) return 0;
      const load = new Map<string, number>();
      roster.forEach((m) => load.set(m, 0));
      state.tickets.forEach((t) => {
        if (t.projectId !== projectId) return;
        const done = p.statuses.find((x) => x.name === t.status)?.category === 'done';
        if (!done && t.assignee && load.has(t.assignee)) load.set(t.assignee, (load.get(t.assignee) || 0) + 1);
      });
      const picks = new Map<string, string>();
      state.tickets.forEach((t) => {
        if (t.projectId !== projectId || (t.assignee || '').trim()) return;
        let pick = roster[0];
        roster.forEach((m) => { if ((load.get(m) || 0) < (load.get(pick) || 0)) pick = m; });
        load.set(pick, (load.get(pick) || 0) + 1);
        picks.set(t.id, pick);
      });
      if (picks.size === 0) return 0;
      setState((s) => ({
        ...s,
        tickets: s.tickets.map((t) => (picks.has(t.id) ? { ...t, assignee: picks.get(t.id) as string, updatedAt: now() } : t))
      }));
      return picks.size;
    };
    const setAssigneeRequired: TicketStoreValue['setAssigneeRequired'] = (projectId, required) =>
      setState((s) => ({
        ...s,
        projects: s.projects.map((p) => (p.id === projectId ? { ...p, assigneeRequired: required } : p))
      }));
    const withMember = (project: TicketProject | undefined, assignee: string): TicketProject | undefined => {
      const n = (assignee || '').trim();
      if (!project || !n || (project.members || []).some((m) => m.toLowerCase() === n.toLowerCase())) return project;
      return { ...project, members: [...(project.members || []), n] };
    };

    // ── Workflow columns ─────────────────────────────────────────────────────
    const isDoneStatus: TicketStoreValue['isDoneStatus'] = (projectId, status) => {
      const p = state.projects.find((x) => x.id === projectId);
      return p?.statuses.find((s) => s.name === status)?.category === 'done';
    };
    const addStatus: TicketStoreValue['addStatus'] = (projectId, input) => {
      const name = input.name.trim();
      if (!name) return null;
      let created: CustomStatus | null = null;
      setState((s) => {
        const p = s.projects.find((x) => x.id === projectId);
        if (!p) return s;
        if (p.statuses.some((x) => x.name.toLowerCase() === name.toLowerCase())) return s;
        created = {
          id: uid(),
          name,
          color: input.color || STATUS_COLOR_PALETTE[p.statuses.length % STATUS_COLOR_PALETTE.length],
          category: input.category || 'inprogress'
        };
        return { ...s, projects: s.projects.map((x) => (x.id === projectId ? { ...x, statuses: [...x.statuses, created as CustomStatus] } : x)) };
      });
      return created;
    };
    const updateStatus: TicketStoreValue['updateStatus'] = (projectId, statusId, patch) =>
      setState((s) => {
        const p = s.projects.find((x) => x.id === projectId);
        if (!p) return s;
        const cur = p.statuses.find((x) => x.id === statusId);
        if (!cur) return s;
        const name = (patch.name ?? cur.name).trim();
        if (!name) return s;
        if (p.statuses.some((x) => x.id !== statusId && x.name.toLowerCase() === name.toLowerCase())) return s;
        const statuses = p.statuses.map((x) => (x.id === statusId ? { ...x, ...patch, name } : x));
        // renaming a column moves its tickets along AND rewrites their history
        // entries (no new entry appended — the meaning didn't change, just the name)
        const tickets = patch.name !== undefined && name !== cur.name
          ? s.tickets.map((t) => (t.projectId === projectId && t.status === cur.name
            ? {
              ...t,
              status: name,
              updatedAt: now(),
              statusHistory: (t.statusHistory || []).map((h) => (h.status === cur.name ? { ...h, status: name } : h))
            }
            : t))
          : s.tickets;
        return { ...s, projects: s.projects.map((x) => (x.id === projectId ? { ...x, statuses } : x)), tickets };
      });
    const deleteStatus: TicketStoreValue['deleteStatus'] = (projectId, statusId, moveTo) =>
      setState((s) => {
        const p = s.projects.find((x) => x.id === projectId);
        if (!p || p.statuses.length <= 1) return s;
        const cur = p.statuses.find((x) => x.id === statusId);
        if (!cur || !p.statuses.some((x) => x.name === moveTo)) return s;
        return {
          ...s,
          projects: s.projects.map((x) => (x.id === projectId ? { ...x, statuses: x.statuses.filter((y) => y.id !== statusId) } : x)),
          tickets: s.tickets.map((t) =>
            t.projectId === projectId && t.status === cur.name
              ? { ...t, status: moveTo, updatedAt: now(), statusHistory: [...(t.statusHistory || []), { status: moveTo, at: now() }] }
              : t
          )
        };
      });
    const moveStatus: TicketStoreValue['moveStatus'] = (projectId, statusId, dir) =>
      setState((s) => {
        const p = s.projects.find((x) => x.id === projectId);
        if (!p) return s;
        const i = p.statuses.findIndex((x) => x.id === statusId);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= p.statuses.length) return s;
        const statuses = [...p.statuses];
        [statuses[i], statuses[j]] = [statuses[j], statuses[i]];
        return { ...s, projects: s.projects.map((x) => (x.id === projectId ? { ...x, statuses } : x)) };
      });
    // drag-drop column reorder from the workflow dialog
    const reorderStatuses: TicketStoreValue['reorderStatuses'] = (projectId, orderedIds) => {
      if (!orderedIds.length) return;
      setState((s) => {
        const p = s.projects.find((x) => x.id === projectId);
        if (!p) return s;
        const byId = new Map(p.statuses.map((x) => [x.id, x]));
        // keep only known ids in the given order, then any missing ones at the end
        const seen = new Set<string>();
        const ordered = orderedIds.filter((id) => byId.has(id) && !seen.has(id) && (seen.add(id), true));
        const statuses = [...ordered.map((id) => byId.get(id)!), ...p.statuses.filter((x) => !seen.has(x.id))];
        if (statuses.length !== p.statuses.length) return s;
        return { ...s, projects: s.projects.map((x) => (x.id === projectId ? { ...x, statuses } : x)) };
      });
    };
    const renameTicketStatus: TicketStoreValue['renameTicketStatus'] = (projectId, from, to) => {
      if (!from || !to || from === to) return 0;
      const n = state.tickets.filter((t) => t.projectId === projectId && t.status === from).length;
      if (!n) return 0;
      setState((s) => ({
        ...s,
        tickets: s.tickets.map((t) => (t.projectId === projectId && t.status === from
          ? {
            ...t,
            status: to,
            updatedAt: now(),
            statusHistory: (t.statusHistory || []).map((h) => (h.status === from ? { ...h, status: to } : h))
          }
          : t))
      }));
      return n;
    };
    // replace the whole column list (backend workflow load) — keeps tickets valid
    const loadStatuses: TicketStoreValue['loadStatuses'] = (projectId, incoming) => {      if (!incoming.length) return;
      setState((s) => {
        const p = s.projects.find((x) => x.id === projectId);
        if (!p) return s;
        const names = new Set(incoming.map((x) => x.name));
        const fallback = incoming[0].name;
        return {
          ...s,
          projects: s.projects.map((x) => (x.id === projectId ? { ...x, statuses: incoming } : x)),
          tickets: s.tickets.map((t) =>
            t.projectId === projectId && !names.has(t.status)
              ? { ...t, status: fallback, updatedAt: now() }
              : t
          )
        };
      });
    };

    const createSprint: TicketStoreValue['createSprint'] = (projectId, input) => {
      const existing = state.sprints.filter((s) => s.projectId === projectId).length;
      const sp: TicketSprint = {
        id: input.id || uid(),
        projectId,
        name: input.name.trim() || `Sprint ${existing + 1}`,
        goal: input.goal || '',
        state: 'backlog',
        startDate: input.startDate,
        endDate: input.endDate,
        createdAt: now()
      };
      setState((s) => ({ ...s, sprints: [...s.sprints, sp] }));
      return sp;
    };
    const updateSprint: TicketStoreValue['updateSprint'] = (id, patch) =>
      setState((s) => ({ ...s, sprints: s.sprints.map((sp) => (sp.id === id ? { ...sp, ...patch } : sp)) }));
    const deleteSprint: TicketStoreValue['deleteSprint'] = (id) =>
      setState((s) => ({
        ...s,
        sprints: s.sprints.filter((sp) => sp.id !== id),
        tickets: s.tickets.map((t) => (t.sprintId === id ? { ...t, sprintId: null, updatedAt: now() } : t))
      }));
    const setSprintState = (id: string, sprintState: SprintState, dates?: { startDate: string; endDate: string }, dest?: SprintDestination) =>
      setState((s) => {
        const target = s.sprints.find((sp) => sp.id === id);
        if (!target) return s;
        let sprints = s.sprints.map((sp) =>
          sp.id === id
            ? {
                ...sp,
                state: sprintState,
                startDate: dates?.startDate || (sprintState === 'active' ? sp.startDate || now() : sp.startDate),
                endDate: dates?.endDate || (sprintState === 'done' ? now() : sp.endDate)
              }
            : sp
        );
        let tickets = s.tickets;
        if (sprintState === 'done') {
          // finished = right-most "done" column; everything else is unfinished work
          const doneNames = new Set(target ? s.projects.find((p) => p.id === target.projectId)?.statuses.filter((x) => x.category === 'done').map((x) => x.name) || [] : []);
          const unfinished = (t: Ticket) => t.sprintId === id && !doneNames.has(t.status);
          // Jira-style: unfinished work goes where the user chose.
          if (!dest || dest.kind === 'backlog') {
            tickets = tickets.map((t) => (unfinished(t) ? { ...t, sprintId: null, updatedAt: now() } : t));
          } else if (dest.kind === 'sprint') {
            tickets = tickets.map((t) => (unfinished(t) ? { ...t, sprintId: dest.sprintId, updatedAt: now() } : t));
          } else if (dest.kind === 'new') {
            const continued: TicketSprint = {
              id: dest.id || uid(),
              projectId: target.projectId,
              name: dest.name.trim() || `${target.name} — continued`,
              goal: dest.goal || target.goal || '',
              state: 'backlog',
              createdAt: now()
            };
            sprints = [...sprints, continued];
            tickets = tickets.map((t) => (unfinished(t) ? { ...t, sprintId: continued.id, updatedAt: now() } : t));
          }
        }
        return { ...s, sprints, tickets };
      });
    const startSprint: TicketStoreValue['startSprint'] = (id, dates) => setSprintState(id, 'active', dates);
    const completeSprint: TicketStoreValue['completeSprint'] = (id, dest) => setSprintState(id, 'done', undefined, dest || { kind: 'backlog' });

    const loadSprints: TicketStoreValue['loadSprints'] = (projectId, incoming) => {
      setState((s) => {
        const others = s.sprints.filter((sp) => sp.projectId !== projectId);
        const normalized = incoming.map((sp) => ({
          ...sp,
          goal: sp.goal || '',
          createdAt: sp.createdAt || now()
        }));
        return { ...s, sprints: [...others, ...normalized] };
      });
    };

    const createTicket: TicketStoreValue['createTicket'] = (projectId, input) => {
      let created: Ticket | null = null;
      setState((s) => {
        const project = s.projects.find((p) => p.id === projectId);
        if (!project) return s;
        const next = (project.counter || 0) + 1;
        const byId: Record<string, Ticket> = {};
        s.tickets.forEach((t) => { byId[t.id] = t; });
        const names = (project.statuses || []).map((x) => x.name);
        const status = (input.status && names.includes(input.status) ? input.status : names[0] || 'Todo') as TicketStatus;
        const draft = {
          id: uid(),
          projectId,
          type: input.type || 'Task',
          parentId: input.parentId ?? null
        } as Ticket;
        created = {
          // Honor a caller-supplied id/key (real-mode create passes the backend
          // uid/key). Generating a fresh local id here orphaned the row: every
          // subsequent API call (delete/update/status) used the temp id and the
          // backend rejected it (500 Invalid UUID string).
          id: (input as Partial<Ticket>).id || uid(),
          key: (input as Partial<Ticket>).key || `${project.key}-${next}`,
          projectId,
          title: input.title.trim(),
          description: input.description || '',
          type: input.type || 'Task',
          status,
          priority: input.priority || 'Medium',
          points: input.points ?? 2,
          assignee: input.assignee || '',
          reporter: input.reporter || '',
          labels: input.labels || [],
          links: input.links || [],
          components: input.components || [],
          parentId: sanitizeParent(draft, byId),
          dueDate: input.dueDate || '',
          moduleUid: input.moduleUid || '',
          testCaseUid: input.testCaseUid || '',
          sprintId: input.sprintId ?? null,
          createdAt: now(),
          updatedAt: now(),
          comments: input.comments || [],
          checklist: input.checklist || [],
          attachments: input.attachments || [],
          statusHistory: [{ status, at: now() }]
        } as Ticket;
        return {
          ...s,
          projects: s.projects.map((p) => {
            if (p.id !== projectId) return p;
            const withCount = { ...p, counter: next };
            // assigning to someone new adds them to the team automatically
            return withMember(withCount, (created as Ticket).assignee) || withCount;
          }),
          tickets: [...s.tickets, created as Ticket]
        };
      });
      return created as unknown as Ticket;
    };

    const loadTickets: TicketStoreValue['loadTickets'] = (projectId, incoming, scopeFilter, append) => {
      setState((s) => {
        // determine which existing tickets to keep
        let others: Ticket[];
        if (append) {
          // paginated infinite-scroll: keep everything already loaded, new batch is merged below (dedupe by id)
          others = s.tickets;
        } else if (scopeFilter && scopeFilter !== 'all') {
          if (scopeFilter === 'backlog') {
            // Keep tickets that are NOT backlog (i.e. tickets in other projects or tickets with a sprintId)
            others = s.tickets.filter(
              (t) => t.projectId !== projectId || (t.sprintId != null && t.sprintId.trim() !== '')
            );
          } else {
            // Specific sprint: keep tickets not in this project OR not in this sprint
            const normFilter = scopeFilter.trim().toLowerCase();
            others = s.tickets.filter(
              (t) => t.projectId !== projectId || (t.sprintId || '').trim().toLowerCase() !== normFilter
            );
          }
        } else {
          // 'all' or undefined: replace all tickets for this project
          others = s.tickets.filter((t) => t.projectId !== projectId);
        }

        const normalized = incoming.map((t) => ({
          ...t,
          comments: t.comments || [],
          checklist: t.checklist || [],
          attachments: t.attachments || [],
          labels: t.labels || [],
          links: t.links || [],
          components: t.components || [],
          parentId: t.parentId || null,
          testCaseUid: t.testCaseUid || '',
          statusHistory: t.statusHistory || []
        }));
        let merged: Ticket[];
        if (append) {
          const seen = new Set(others.map((t) => t.id));
          const fresh = normalized.filter((t) => !seen.has(t.id));
          merged = [...others, ...fresh];
        } else {
          merged = [...others, ...normalized];
        }
        // update project counter to keep keys unique for new local creations
        const max = merged.reduce((acc, t) => {
          if (t.projectId !== projectId) return acc;
          const num = parseInt((t.key || '').split('-').pop() || '0', 10);
          return !isNaN(num) && num > acc ? num : acc;
        }, 0);
        return {
          ...s,
          projects: s.projects.map((p) =>
            p.id === projectId ? { ...p, counter: Math.max(p.counter || 0, max) } : p
          ),
          tickets: merged
        };
      });
    };

    const updateTicket: TicketStoreValue['updateTicket'] = (id, patch) =>
      setState((s) => {
        const byId: Record<string, Ticket> = {};
        s.tickets.forEach((t) => { byId[t.id] = t; });
        let projects = s.projects;
        // assigning to someone new adds them to the team automatically
        if (patch.assignee !== undefined) {
          const t = byId[id];
          const proj = t && projects.find((p) => p.id === t.projectId);
          const updated = withMember(proj, patch.assignee || '');
          if (updated && proj && updated !== proj) projects = projects.map((p) => (p.id === proj.id ? updated : p));
        }
        return {
          ...s,
          projects,
          tickets: s.tickets.map((t) => {
            if (t.id !== id) return t;
            const next = { ...t, ...patch, updatedAt: now() } as Ticket;
            if (patch.status && patch.status !== t.status) {
              next.statusHistory = [...(t.statusHistory || []), { status: patch.status, at: now() }];
              const isDone = patch.status.toLowerCase() === 'done';
              if (isDone) {
                next.resolution = patch.resolution || t.resolution || 'Fixed';
                next.resolvedAt = t.resolvedAt || now();
              } else {
                next.resolution = undefined;
                next.resolvedAt = undefined;
              }
            } else if (patch.resolution !== undefined) {
              next.resolution = patch.resolution || undefined;
            }
            // keep the hierarchy valid when type or parent changes
            if (patch.type !== undefined || patch.parentId !== undefined) {
              next.parentId = sanitizeParent(next, byId);
            }
            return next;
          })
        };
      });
    const deleteTicket: TicketStoreValue['deleteTicket'] = (id) =>
      setState((s) => ({
        ...s,
        // deleting a parent unlinks its children instead of orphaning them; drop issue links to it too
        tickets: s.tickets
          .filter((t) => t.id !== id)
          .map((t) => ({
            ...t,
            parentId: t.parentId === id ? null : t.parentId,
            links: (t.links || []).filter((l) => l.targetId !== id),
            updatedAt: now()
          }))
      }));
    const addLink: TicketStoreValue['addLink'] = (sourceId, targetId, relation) => {
      if (!sourceId || !targetId || sourceId === targetId) return null;
      const byId: Record<string, Ticket> = {};
      state.tickets.forEach((t) => { byId[t.id] = t; });
      const source = byId[sourceId];
      const target = byId[targetId];
      if (!source || !target) return null;
      if ((source.links || []).some((l) => l.targetId === targetId)) return null; // already linked
      const link: TicketLink = { id: uid(), targetId, relation };
      const mirror: TicketLink = { id: uid(), targetId: sourceId, relation: inverseRelation(relation) };
      const sourceLinks = [...(source.links || []), link];
      const targetLinks = [...(target.links || []), mirror];
      setState((s) => ({
        ...s,
        tickets: s.tickets.map((t) =>
          t.id === sourceId ? { ...t, links: sourceLinks, updatedAt: now() }
          : t.id === targetId ? { ...t, links: targetLinks, updatedAt: now() }
          : t
        )
      }));
      return { sourceLinks, targetLinks };
    };
    const removeLink: TicketStoreValue['removeLink'] = (sourceId, linkId) => {
      const byId: Record<string, Ticket> = {};
      state.tickets.forEach((t) => { byId[t.id] = t; });
      const source = byId[sourceId];
      const link = source?.links?.find((l) => l.id === linkId);
      if (!source || !link) return null;
      const target = byId[link.targetId];
      const sourceLinks = (source.links || []).filter((l) => l.id !== linkId);
      const targetLinks = target
        ? (target.links || []).filter((l) => !(l.targetId === sourceId && l.relation === inverseRelation(link.relation)))
        : [];
      setState((s) => ({
        ...s,
        tickets: s.tickets.map((t) =>
          t.id === sourceId ? { ...t, links: sourceLinks, updatedAt: now() }
          : target && t.id === target.id ? { ...t, links: targetLinks, updatedAt: now() }
          : t
        )
      }));
      return { sourceLinks, targetLinks };
    };
    const moveTicket: TicketStoreValue['moveTicket'] = (id, status, resolution) => {
      const isDone = status.toLowerCase() === 'done';
      setState((s) => ({
        ...s,
        tickets: s.tickets.map((t) =>
          t.id === id
            ? {
                ...t,
                status,
                resolution: isDone ? (resolution || t.resolution || 'Fixed') : undefined,
                resolvedAt: isDone ? (t.resolvedAt || now()) : undefined,
                updatedAt: now(),
                statusHistory: [...(t.statusHistory || []), { status, at: now() }]
              }
            : t
        )
      }));
    };
    const assignSprint: TicketStoreValue['assignSprint'] = (ticketId, sprintId) =>
      setState((s) => ({
        ...s,
        tickets: s.tickets.map((t) => (t.id === ticketId ? { ...t, sprintId, updatedAt: now() } : t))
      }));
    const moveToBacklog: TicketStoreValue['moveToBacklog'] = (ticketId) => assignSprint(ticketId, null);
    const addComment: TicketStoreValue['addComment'] = (ticketId, author, text, id) =>
      setState((s) => ({
        ...s,
        tickets: s.tickets.map((t) =>
          t.id === ticketId
            ? { ...t, comments: [...t.comments, { id: id || uid(), author: author || getCurrentUserName(), text, createdAt: now() }], updatedAt: now() }
            : t
        )
      }));
    const deleteComment: TicketStoreValue['deleteComment'] = (ticketId, commentId) =>
      setState((s) => ({
        ...s,
        tickets: s.tickets.map((t) =>
          t.id === ticketId ? { ...t, comments: t.comments.filter((c) => c.id !== commentId), updatedAt: now() } : t
        )
      }));
    const updateComment: TicketStoreValue['updateComment'] = (ticketId, commentId, text) =>
      setState((s) => ({
        ...s,
        tickets: s.tickets.map((t) =>
          t.id === ticketId
            ? { ...t, comments: t.comments.map((c) => (c.id === commentId ? { ...c, text } : c)), updatedAt: now() }
            : t
        )
      }));
    const addAttachment: TicketStoreValue['addAttachment'] = async (ticketId, file, author) => {
      const dataUrl = await fileToDataUrl(file);
      const attachment = {
        id: uid(),
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        dataUrl,
        uploadedBy: author || getCurrentUserName(),
        uploadedAt: now()
      };
      setState((s) => ({
        ...s,
        tickets: s.tickets.map((t) =>
          t.id === ticketId
            ? { ...t, attachments: [...(t.attachments || []), attachment], updatedAt: now() }
            : t
        )
      }));
    };
    const deleteAttachment: TicketStoreValue['deleteAttachment'] = (ticketId, attachmentId) =>
      setState((s) => ({
        ...s,
        tickets: s.tickets.map((t) =>
          t.id === ticketId
            ? { ...t, attachments: (t.attachments || []).filter((a) => a.id !== attachmentId), updatedAt: now() }
            : t
        )
      }));
    return {
      ...state,
      createProject,
      ensureLinkedProject,
      updateProject,
      deleteProject,
      clearStore,
      pruneDeletedProjects,
      addMember,
      removeMember,
      autoAssign,
      setAssigneeRequired,
      addStatus,
      updateStatus,
      deleteStatus,
      moveStatus,
      reorderStatuses,
      loadStatuses,
      renameTicketStatus,
      isDoneStatus,
      createSprint,
      updateSprint,
      deleteSprint,
      startSprint,
      completeSprint,
      loadSprints,
      createTicket,
      loadTickets,
      updateTicket,
      deleteTicket,
      moveTicket,
      assignSprint,
      moveToBacklog,
      addComment,
      updateComment,
      deleteComment,
      addLink,
      removeLink,
      addAttachment,
      deleteAttachment
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return <TicketStoreCtx.Provider value={value}>{children}</TicketStoreCtx.Provider>;
}

export function useTickets() {
  const ctx = useContext(TicketStoreCtx);
  if (!ctx) throw new Error('useTickets must be used inside TicketStoreProvider');
  return ctx;
}
